import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import classnames from 'classnames/bind';
import { CSSTransition } from 'react-transition-group';

import useEscKeyAction from '../../../hooks/useEscKeyAction';

// Components
import { Close } from '../../icons';

// Styles
import styles from './modal.module.scss';

const cx = classnames.bind(styles);

const Modal = ({ isOpen, onClose, children }) => {
  const modalRef = useRef();

  const onModalClose = () => {
    enableBodyScroll(modalRef.current);
    onClose();
  };

  useEscKeyAction(onModalClose);

  // to prevent parent scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      disableBodyScroll(modalRef.current);
    }
  }, [isOpen]);

  return (
    <CSSTransition
      in={isOpen}
      timeout={200}
      classNames={{
        enter: styles.modalEnter,
        enterActive: styles.modalEnterActive,
        exit: styles.modalExit,
        exitActive: styles.modalExitActive,
      }}
    >
      <div className={cx(styles.modalWrapper)} data-testid="modal-wrapper">
        <div
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          data-testid="modal"
          ref={modalRef}
        >
          <button
            type="button"
            className={styles.modalCloseBtn}
            aria-label="Close"
            onClick={onModalClose}
            data-testid="modal-close-btn"
          >
            <Close />
          </button>

          <div className={styles.modalBody}>{children}</div>
        </div>
      </div>
    </CSSTransition>
  );
};

Modal.defaultProps = {
  isOpen: false,
  onClose: () => {},
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default Modal;
